interface TrackPosition {
  top: string
  left: string
  width: string
  height: string
  transform: string
  zIndex?: string
}

// Constants
const GAPS = {
  NORMAL: 16,
  FIT: 8,
} as const

const UNFOCUSED_TRACK_HEIGHT = '125px'

// Utility functions for width calculations
const createWidthCalc = (percentage: number, gapMultiplier: number, gap: number): string =>
  `calc(${percentage}% - ${(gap * gapMultiplier) / (100 / percentage)}px)`

const createHeightCalc = (percentage: number, gap: number): string =>
  `calc(${percentage}% - ${gap / 2}px)`

const getGap = (isSmallMode: boolean): number => (isSmallMode ? GAPS.FIT : GAPS.NORMAL)

// Width and height helper functions
const getCommonDimensions = (gap: number) => ({
  width16: createWidthCalc(100 / 6, 5, gap),
  width20: createWidthCalc(20, 3, gap),
  width25: createWidthCalc(25, 3, gap),
  width33: createWidthCalc(100 / 3, 2, gap),
  width50: createWidthCalc(50, 1, gap),
  height25: createHeightCalc(25, gap),
  height33: createHeightCalc(100 / 3, gap),
  height50: createHeightCalc(50, gap),
})

// Focused track utilities
const getUnfocusedTrackAutomaticWidth = (
  unfocusedTracks: number,
  dimensions: ReturnType<typeof getCommonDimensions>
): string => {
  const widthMap: Record<string, string> = {
    1: dimensions.width50,
    2: dimensions.width50,
    3: dimensions.width33,
    4: dimensions.width25,
    5: dimensions.width20,
    6: dimensions.width16,
  }
  return widthMap[unfocusedTracks] || dimensions.width33
}

const getUnfocusedTrackWidth = (
  unfocusedTracks: number,
  isSmallMode: boolean,
  isMediumMode: boolean,
  dimensions: ReturnType<typeof getCommonDimensions>
): string => {
  if (isSmallMode) {
    return getUnfocusedTrackAutomaticWidth(unfocusedTracks, dimensions)
  }

  const widthMap: Record<string, string> = {
    1: '250px',
    2: '250px',
    3: isMediumMode ? '240px' : '250px',
    4: isMediumMode ? '180px' : '240px',
    5: isMediumMode ? '140px' : '190px',
    6: isMediumMode ? '115px' : '155px',
  }

  return widthMap[unfocusedTracks] || dimensions.width33
}

// Focused track position generators
const createFocusedTrackPosition = (): TrackPosition => ({
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  transform: 'translate(0, 0)',
  zIndex: '1',
})

const createOverlayUnfocusedPosition = (
  unfocusedIndex: number,
  unfocusedTrackWidth: string,
  gap: number
): TrackPosition => ({
  top: `calc(100% - ${UNFOCUSED_TRACK_HEIGHT} - ${gap}px)`,
  left: `calc(${gap}px + ${unfocusedIndex} * (${unfocusedTrackWidth} + ${gap}px))`,
  width: unfocusedTrackWidth,
  height: UNFOCUSED_TRACK_HEIGHT,
  transform: 'translate(0, 0)',
  zIndex: '2',
})

const createBottomUnfocusedPosition = (
  unfocusedIndex: number,
  unfocusedTracks: number,
  unfocusedTrackWidth: string,
  gap: number
): TrackPosition => {
  const totalUnfocusedWidth = `calc(${unfocusedTracks} * ${unfocusedTrackWidth} + ${
    unfocusedTracks - 1
  } * ${gap}px)`
  const startOffset = `calc((100% - (${totalUnfocusedWidth})) / 2)`

  return {
    top: `calc(100% - ${UNFOCUSED_TRACK_HEIGHT})`,
    left: `calc(${startOffset} + ${unfocusedIndex} * (${unfocusedTrackWidth} + ${gap}px))`,
    width: unfocusedTrackWidth,
    height: UNFOCUSED_TRACK_HEIGHT,
    transform: 'translate(0, 0)',
  }
}

const createFocusedMainPosition = (gap: number): TrackPosition => ({
  top: '0',
  left: '0',
  width: '100%',
  height: `calc(100% - ${UNFOCUSED_TRACK_HEIGHT} - ${gap}px)`,
  transform: 'translate(0, 0)',
})

// Track position generators for different layouts
const createSingleTrackLayout = (): TrackPosition[] => [
  {
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    transform: 'translate(0, 0)',
  },
]

const createDualTrackLayout = (
  isSmallMode: boolean,
  dimensions: ReturnType<typeof getCommonDimensions>,
  gap: number
): TrackPosition[] => [
  {
    top: '0',
    left: '0',
    width: isSmallMode ? '100%' : dimensions.width50,
    height: isSmallMode ? dimensions.height50 : '100%',
    transform: 'translate(0, 0)',
  },
  {
    top: isSmallMode ? `calc(50% + ${gap / 2}px)` : '0',
    left: '0',
    width: isSmallMode ? '100%' : dimensions.width50,
    height: isSmallMode ? dimensions.height50 : '100%',
    transform: `translate(${isSmallMode ? '0' : `calc(100% + ${gap}px)`}, 0)`,
  },
]

const createTripleTrackLayout = (
  isSmallMode: boolean,
  dimensions: ReturnType<typeof getCommonDimensions>,
  gap: number
): TrackPosition[] => [
  {
    top: '0',
    left: isSmallMode ? '0' : '50%',
    width: isSmallMode ? '100%' : dimensions.width50,
    height: isSmallMode ? dimensions.height33 : dimensions.height50,
    transform: `translate(${isSmallMode ? '0' : 'calc(-50% + 8px)'}, 0)`,
  },
  {
    top: isSmallMode ? `calc(33.33% + ${gap / 2}px)` : `calc(50% + ${gap / 2}px)`,
    left: '0',
    width: isSmallMode ? '100%' : dimensions.width50,
    height: isSmallMode ? dimensions.height33 : dimensions.height50,
    transform: 'translate(0, 0)',
  },
  {
    top: isSmallMode ? `calc(66.66% + ${gap}px)` : `calc(50% + ${gap / 2}px)`,
    left: '0',
    width: isSmallMode ? '100%' : dimensions.width50,
    height: isSmallMode ? dimensions.height33 : dimensions.height50,
    transform: `translate(${isSmallMode ? '0' : `calc(100% + ${gap}px)`}, 0)`,
  },
]

const createQuadTrackLayout = (
  isSmallMode: boolean,
  dimensions: ReturnType<typeof getCommonDimensions>,
  gap: number
): TrackPosition[] => [
  {
    top: '0',
    left: '0',
    width: isSmallMode ? '100%' : dimensions.width50,
    height: isSmallMode ? dimensions.height25 : dimensions.height50,
    transform: 'translate(0, 0)',
  },
  {
    top: isSmallMode ? `calc(25% + ${gap / 2}px)` : '0',
    left: '0',
    width: isSmallMode ? '100%' : dimensions.width50,
    height: isSmallMode ? dimensions.height25 : dimensions.height50,
    transform: `translate(${isSmallMode ? '0' : `calc(100% + ${gap}px)`}, 0)`,
  },
  {
    top: isSmallMode ? `calc(50% + ${gap}px)` : `calc(50% + ${gap / 2}px)`,
    left: '0',
    width: isSmallMode ? '100%' : dimensions.width50,
    height: isSmallMode ? dimensions.height25 : dimensions.height50,
    transform: 'translate(0, 0)',
  },
  {
    top: isSmallMode ? `calc(75% + ${gap * 1.5}px)` : `calc(50% + ${gap / 2}px)`,
    left: '0',
    width: isSmallMode ? '100%' : dimensions.width50,
    height: isSmallMode ? dimensions.height25 : dimensions.height50,
    transform: `translate(${isSmallMode ? '0' : `calc(100% + ${gap}px)`}, 0)`,
  },
]

const createPentaTrackLayout = (
  isSmallMode: boolean,
  dimensions: ReturnType<typeof getCommonDimensions>,
  gap: number
): TrackPosition[] => {
  const doubleGap = gap * 2

  return [
    {
      top: '0',
      left: '0',
      width: isSmallMode ? '100%' : dimensions.width50,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: 'translate(0, 0)',
    },
    {
      top: isSmallMode ? `calc(33.33% + ${gap / 2}px)` : '0',
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width50,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(${isSmallMode ? '0' : `calc(100% + ${gap}px)`}, 0)`,
    },
    {
      top: isSmallMode ? `calc(33.33% + ${gap / 2}px)` : `calc(50% + ${gap / 2}px)`,
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width33,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(${isSmallMode ? `calc(100% + ${gap}px)` : '0'}, 0)`,
    },
    {
      top: isSmallMode ? `calc(66.66% + ${gap}px)` : `calc(50% + ${gap / 2}px)`,
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width33,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(${isSmallMode ? '0' : `calc(100% + ${gap}px)`}, 0)`,
    },
    {
      top: isSmallMode ? `calc(66.66% + ${gap}px)` : `calc(50% + ${gap / 2}px)`,
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width33,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(${
        isSmallMode ? `calc(100% + ${gap}px)` : `calc(200% + ${doubleGap}px)`
      }, 0)`,
    },
  ]
}

const createHexaTrackLayout = (
  isSmallMode: boolean,
  dimensions: ReturnType<typeof getCommonDimensions>,
  gap: number
): TrackPosition[] => {
  const doubleGap = gap * 2

  return [
    {
      top: '0',
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width33,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: 'translate(0, 0)',
    },
    {
      top: '0',
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width33,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(calc(100% + ${gap}px), 0)`,
    },
    {
      top: isSmallMode ? `calc(33.33% + ${gap / 2}px)` : '0',
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width33,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(${isSmallMode ? '0' : `calc(200% + ${doubleGap}px)`}, 0)`,
    },
    {
      top: isSmallMode ? `calc(33.33% + ${gap / 2}px)` : `calc(50% + ${gap / 2}px)`,
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width33,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(${isSmallMode ? `calc(100% + ${gap}px)` : '0'}, 0)`,
    },
    {
      top: isSmallMode ? `calc(66.66% + ${gap}px)` : `calc(50% + ${gap / 2}px)`,
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width33,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(${isSmallMode ? '0' : `calc(100% + ${gap}px)`}, 0)`,
    },
    {
      top: isSmallMode ? `calc(66.66% + ${gap}px)` : `calc(50% + ${gap / 2}px)`,
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width33,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(${
        isSmallMode ? `calc(100% + ${gap}px)` : `calc(200% + ${doubleGap}px)`
      }, 0)`,
    },
  ]
}

const createSeptaTrackLayout = (
  isSmallMode: boolean,
  dimensions: ReturnType<typeof getCommonDimensions>,
  gap: number
): TrackPosition[] => {
  const doubleGap = gap * 2
  const tripleGap = gap * 3

  return [
    {
      top: '0',
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width33,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: 'translate(0, 0)',
    },
    {
      top: '0',
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width33,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(calc(100% + ${gap}px), 0)`,
    },
    {
      top: isSmallMode ? `calc(33.33% + ${gap / 2}px)` : '0',
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width33,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(${isSmallMode ? '0' : `calc(200% + ${doubleGap}px)`}, 0)`,
    },
    {
      top: isSmallMode ? `calc(33.33% + ${gap / 2}px)` : `calc(50% + ${gap / 2}px)`,
      left: '0',
      width: isSmallMode ? dimensions.width50 : dimensions.width25,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(${isSmallMode ? `calc(100% + ${gap}px)` : '0'}, 0)`,
    },
    {
      top: isSmallMode ? `calc(66.66% + ${gap}px)` : `calc(50% + ${gap / 2}px)`,
      left: '0',
      width: isSmallMode ? dimensions.width33 : dimensions.width25,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(${isSmallMode ? '0' : `calc(100% + ${gap}px)`}, 0)`,
    },
    {
      top: isSmallMode ? `calc(66.66% + ${gap}px)` : `calc(50% + ${gap / 2}px)`,
      left: '0',
      width: isSmallMode ? dimensions.width33 : dimensions.width25,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(${
        isSmallMode ? `calc(100% + ${gap}px)` : `calc(200% + ${doubleGap}px)`
      }, 0)`,
    },
    {
      top: isSmallMode ? `calc(66.66% + ${gap}px)` : `calc(50% + ${gap / 2}px)`,
      left: isSmallMode ? '50%' : '0',
      width: isSmallMode ? dimensions.width33 : dimensions.width25,
      height: isSmallMode ? dimensions.height33 : dimensions.height50,
      transform: `translate(${
        isSmallMode ? `calc(50% + ${gap}px)` : `calc(300% + ${tripleGap}px)`
      }, 0)`,
    },
  ]
}

// Main layout generator
const getRegularTrackPositions = (
  numberOfTracks: number,
  isSmallMode: boolean,
  dimensions: ReturnType<typeof getCommonDimensions>,
  gap: number
): TrackPosition[] => {
  const layoutGenerators: Record<string, () => TrackPosition[]> = {
    1: () => createSingleTrackLayout(),
    2: () => createDualTrackLayout(isSmallMode, dimensions, gap),
    3: () => createTripleTrackLayout(isSmallMode, dimensions, gap),
    4: () => createQuadTrackLayout(isSmallMode, dimensions, gap),
    5: () => createPentaTrackLayout(isSmallMode, dimensions, gap),
    6: () => createHexaTrackLayout(isSmallMode, dimensions, gap),
    7: () => createSeptaTrackLayout(isSmallMode, dimensions, gap),
  }

  return layoutGenerators[numberOfTracks]?.() || []
}

// Focused track position generator
const getFocusedTrackPositions = (
  numberOfTracks: number,
  focusedTrackIndex: number,
  isSmallMode: boolean,
  isMediumMode: boolean,
  dimensions: ReturnType<typeof getCommonDimensions>,
  gap: number
): TrackPosition[] => {
  const unfocusedTracks = numberOfTracks - 1
  const unfocusedTrackWidth = getUnfocusedTrackWidth(
    unfocusedTracks,
    isSmallMode,
    isMediumMode,
    dimensions
  )

  return Array(numberOfTracks)
    .fill(null)
    .map((_, index) => {
      if (index === focusedTrackIndex) {
        return unfocusedTracks <= 1 ? createFocusedTrackPosition() : createFocusedMainPosition(gap)
      }

      const unfocusedIndex = index > focusedTrackIndex ? index - 1 : index

      return unfocusedTracks <= 1
        ? createOverlayUnfocusedPosition(unfocusedIndex, unfocusedTrackWidth, gap)
        : createBottomUnfocusedPosition(unfocusedIndex, unfocusedTracks, unfocusedTrackWidth, gap)
    })
}

// Main export function
export const getTrackPositions = (
  numberOfTracks: number,
  isSmallMode: boolean,
  isMediumMode: boolean,
  focusedTrackIndex?: number
): TrackPosition[] => {
  const gap = getGap(isSmallMode)
  const dimensions = getCommonDimensions(gap)

  if (focusedTrackIndex !== undefined) {
    return getFocusedTrackPositions(
      numberOfTracks,
      focusedTrackIndex,
      isSmallMode,
      isMediumMode,
      dimensions,
      gap
    )
  }

  return getRegularTrackPositions(numberOfTracks, isSmallMode, dimensions, gap)
}
